import { useEffect, useMemo, useState } from 'react';
import { ActLink } from '../ui/ActLink';
import { AddButton } from '../ui/AddButton';
import { useAiScope } from '../assistant/AiButton';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ACTIVITY_TYPES, TYPE_LABELS, can, type ActivityType, type SessionUser } from '@dpw/shared';
import { useSpace, useSpaceActivities } from '../lib/queries';
import { LayoutGroup, motion } from 'motion/react';
import { BriefPanel } from '../ui/BriefPanel';
import { Skel, SkelList } from '../ui/motion';
import { Strip } from '../ui/Strip';
import { useHotkeys } from '../lib/keys';
import { spring } from '../lib/motion';
import { ActivityTable } from '../ui/ActivityTable';
import { Avatar, ErrorNote, HEALTH, Progress, StatusPill } from '../ui/bits';
import { NewActivity } from '../ui/NewActivity';
import { QuickAdd } from '../ui/QuickAdd';
import { NewTrack } from '../ui/NewTrack';
import { ContextPanel } from '../ui/ContextPanel';
import { MembersSheet, managesMembers } from '../ui/Members';
import { dayLabel, localDay, relDay, shortDay } from '../lib/format';
import { usePhone } from '../lib/media';
import { PhoneSpace } from '../phone/Space';
import { SpaceKnowledge } from '../kb/SpaceKnowledge';
import { useFolders } from '../kb/hooks';
import { MoreMenu } from '../ui/Menu';
import { NewDot } from '../ui/NewDot';
import { useMarkSeen, useNewSince } from '../lib/seen';

export function SpacePage({ me }: { me: SessionUser }) {
  const { id = '' } = useParams();
  const space = useSpace(id);
  useAiScope(space.data ? { spaceId: space.data.id, label: space.data.name } : null);
  // ?tab=context opens the Context tab (approved context cards link here); ?tab=requests the Client requests view.
  const [params, setParams] = useSearchParams();
  // The list's filters can come in the address too (the assistant's "show my tasks in Data Readiness"):
  // ?track=ID&type=task&mine=1&done=1.
  const [track, setTrack] = useState<string>(params.get('track') ?? 'all');
  const [type, setType] = useState<ActivityType | 'all'>((ACTIVITY_TYPES as readonly string[]).includes(params.get('type') ?? '') ? params.get('type') as ActivityType : 'all');
  const [mine, setMine] = useState(params.get('mine') === '1');
  const [done, setDone] = useState(params.get('done') === '1');
  const filterKey = ['track', 'type', 'mine', 'done'].map(k => params.get(k) ?? '').join('|');
  useEffect(() => {
    if (filterKey === '|||') return;
    setTrack(params.get('track') ?? 'all');
    setType((ACTIVITY_TYPES as readonly string[]).includes(params.get('type') ?? '') ? params.get('type') as ActivityType : 'all');
    setMine(params.get('mine') === '1'); setDone(params.get('done') === '1');
    // Only when the address says so (the assistant opened it this way), not on every visit.
  }, [filterKey]);
  const tab = (['activities', 'context', 'knowledge'] as const).find(t => t === params.get('tab')) ?? 'activities';
  const showRequests = params.get('tab') === 'requests';
  const setTab = (t: 'activities' | 'requests' | 'context' | 'knowledge') => setParams(t === 'activities' ? {} : { tab: t }, { replace: true });
  const folders = useFolders({ spaceId: id });
  // New since you looked (6.3): a dot on each track card and row with something new. Opening the space clears nothing;
  // opening a track card clears the track's own dot, opening an activity its row. "Mark all as seen" clears the space.
  const dots = useNewSince();
  const markSeen = useMarkSeen();
  const pickTrack = (t: string) => { setTrack(t); if (t !== 'all' && dots.trackIsNew(t)) markSeen('track', t); };
  const [sheet, setSheet] = useState<'none' | 'activity' | 'track' | 'members'>('none');
  useHotkeys({ n: () => { if (can(me.role, 'activity.write')) setSheet('activity'); } });
  const phone = usePhone();
  const acts = useSpaceActivities(id, { trackId: track === 'all' ? undefined : track, type: type === 'all' ? undefined : type, mine, includeDone: done });
  const requests = useSpaceActivities(id, { clientRequests: true, includeDone: true });
  const openRequests = requests.data?.filter(a => a.status !== 'done').length ?? 0;
  const upcoming = useSpaceActivities(id, {});
  const coming = useMemo(() => (upcoming.data ?? []).filter(a => a.startsAt || a.dueDate)
    .map(a => ({ a, d: a.startsAt ? localDay(a.startsAt) : a.dueDate! })).filter(x => x.d >= localDay(new Date().toISOString()))
    .sort((x, y) => x.d.localeCompare(y.d)).slice(0, 4), [upcoming.data]);

  if (space.isLoading) return <SpaceSkeleton />;
  if (space.error || !space.data) return <ErrorNote error={space.error} />;
  const s = space.data;
  if (phone) return <PhoneSpace s={s} me={me} />;
  const total = s.tracks.reduce((n, t) => n + t.total, 0);
  const open = s.tracks.reduce((n, t) => n + t.open, 0);
  const sel = s.tracks.find(t => t.id === track);

  // Quick add, in words: the selected track (or the first one) unless the line names another.
  const quickTrack = sel ?? s.tracks[0];

  return (
    <>
      <div className="hdr">
        <div className="htl">
          <span className="xs mut">Spaces /</span>
          <h1>{s.name}</h1>
          <span className="xs mut">{[s.clientName, `${s.tracks.length} tracks`, `${total} activities`].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="hact">
          {managesMembers(me, s)
            ? <button type="button" className="stack stackb" onClick={() => setSheet('members')} aria-label={`Members, ${s.members.length} people`}>{s.members.slice(0, 6).map(m => <Avatar key={m.id} p={m} />)}</button>
            : <div className="stack">{s.members.slice(0, 6).map(m => <Avatar key={m.id} p={m} />)}</div>}
          {can(me.role, 'activity.write') && <AddButton onClick={() => setSheet('activity')}>New activity</AddButton>}
          <MoreMenu label="More for this space">
            {close => <>
              {managesMembers(me, s) && <button type="button" role="menuitem" className="moreit" onClick={() => { setSheet('members'); close(); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M9 8a3 3 0 1 0 0-.01M3 20a6 6 0 0 1 12 0M17 9a2.5 2.5 0 1 0 0-.01M15.5 14.5A5 5 0 0 1 21 19" /></svg>People</button>}
              <button type="button" role="menuitem" className="moreit" disabled={!dots.space(s.id)} onClick={() => { markSeen('space', s.id); close(); }}><IconSeen />Mark all as seen</button>
            </>}
          </MoreMenu>
        </div>
      </div>

      <LayoutGroup id="space-tabs">
        <div className="tabs3" role="tablist" aria-label="Space">
          {([['activities', 'Activities', open], ['context', 'Context', 0], ['knowledge', 'Knowledge', folders.data?.length ?? 0]] as const).map(([k, l, n]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {l}{n ? <span className="ct">{n}</span> : null}
              {tab === k && <motion.span layoutId="space-tab" className="tabmark" transition={spring.snappy} />}
            </button>
          ))}
        </div>
      </LayoutGroup>
      {tab === 'activities' && !showRequests && <>
      {/* The space brief and Coming up; asking is the violet AI button at the top right, looking at this space. */}
      <div className="toprow two">
        <BriefPanel kind="space" id={s.id} />
        <div className="mini">
          <div className="rowc btwc"><b className="sm" style={{ fontWeight: 600 }}>Coming up</b><Link className="xs mut" to="/calendar">Calendar</Link></div>
          <div>
            {coming.length ? coming.map(({ a, d }) => (
              <ActLink className="cu" key={a.id} id={a.id}><span className="d">{relDay(d) === 'Today' || relDay(d) === 'Tomorrow' ? relDay(d) : shortDay(d)}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                <span className={`dt ${a.status === 'at_risk' || a.isOverdue ? 'red' : a.status === 'in_progress' ? 'amb' : 'neu'}`} style={{ marginTop: 0 }} /></ActLink>
            )) : <div className="xs mut">Nothing dated yet.</div>}
          </div>
        </div>
      </div>

      <div className="strip" role="tablist" aria-label="Tracks">
        <button className={`tk${track === 'all' ? ' on' : ''}`} onClick={() => pickTrack('all')} role="tab" aria-selected={track === 'all'}>
          <span className="nm"><i style={{ background: 'var(--acc)' }} /><span>All tracks</span></span>
          <span className="meta"><span>{total} activities, {open} open</span></span>
          <Progress value={total ? Math.round(100 * (total - open) / total) : 0} color="var(--acc)" />
        </button>
        {s.tracks.map(t => (
          <button key={t.id} className={`tk${track === t.id ? ' on' : ''}`} onClick={() => pickTrack(t.id)} role="tab" aria-selected={track === t.id} data-new={dots.track(t.id) || undefined}>
            <span className="nm"><i style={{ background: t.color }} /><span>{t.name}</span>{dots.track(t.id) && <NewDot />}</span>
            <span className="meta"><span>{t.open} open of {t.total} · {HEALTH[t.health][0]}</span>{t.lead && <span className="who2" style={{ width: 20, height: 20, fontSize: 9 }} title={t.lead.name}>{t.lead.initials}</span>}</span>
            <Progress value={t.total ? Math.round(100 * (t.total - t.open) / t.total) : 0} color={t.color} />
          </button>
        ))}
        {can(me.role, 'track.write') && <button className="tk add" onClick={() => setSheet('track')}>+ New track</button>}
      </div>

      </>}

      {tab === 'context' ? <ContextPanel kind="space" id={s.id} spaceOf={s.id} /> : tab === 'knowledge' ? <SpaceKnowledge s={s} me={me} /> : !showRequests ? (
        <div className="tblwrap">
          <div className="tblhead">
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="rowc" style={{ gap: 8 }}><b style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.2px' }}>{sel ? sel.name : 'All activities'}</b><span className="xs mut">{acts.data?.length ?? 0} shown</span></div>
              {sel && <div className="xs mut" style={{ marginTop: 3 }}>{sel.lead ? `Lead ${sel.lead.name} · ` : ''}{sel.open} open of {sel.total} · <Link to={`/tracks/${sel.id}`} style={{ color: 'var(--acc-tx)' }}>Open track page</Link></div>}
            </div>
            <Strip className="segstrip">
              <div className="seg2">
                <button className={type === 'all' ? 'on' : ''} onClick={() => setType('all')}>All types</button>
                {ACTIVITY_TYPES.map(t => <button key={t} className={type === t ? 'on' : ''} onClick={() => setType(t)}>{TYPE_LABELS[t]}s</button>)}
              </div>
            </Strip>
            <div className="seg2" style={{ marginLeft: 'auto' }}>
              <button className={mine ? 'on' : ''} onClick={() => setMine(!mine)} aria-pressed={mine}>Mine</button>
              <button className={done ? 'on' : ''} onClick={() => setDone(!done)} aria-pressed={done}>Show done</button>
              <button onClick={() => setTab('requests')} aria-pressed={false}>Client requests{openRequests ? ` ${openRequests}` : ''}</button>
            </div>
          </div>
          {can(me.role, 'activity.write') && quickTrack && <QuickAdd key={s.id} space={s} me={me} trackId={quickTrack.id} />}
          {acts.isLoading ? <SkelList rows={5} variant="conv" /> : <ActivityTable rows={acts.data ?? []} showTrack={!sel} />}
        </div>
      ) : (
        <div className="tblwrap">
          <div className="tblhead"><b style={{ fontSize: 16, fontWeight: 600 }}>Client requests</b><span className="xs mut">Anything requested by someone at {s.clientName ?? 'the client'}</span>
            <div className="seg2" style={{ marginLeft: 'auto' }}><button className="on" onClick={() => setTab('activities')} aria-pressed={true}>Client requests{openRequests ? ` ${openRequests}` : ''}</button></div></div>
          {!requests.data?.length ? <div className="empty">No client requests yet. Set "Requested by" to a client contact on any activity.</div> : (
            <div className="atbox"><table className="at">
              <thead><tr><th className="c-act">What</th><th className="c-meta">Asked by</th><th className="c-meta">When asked</th><th className="c-meta">Promised</th><th className="c-stat">Status</th></tr></thead>
              <tbody>{requests.data.map(a => (
                <tr className="arow" key={a.id}>
                  <td className="c-act"><ActLink className="ttl" id={a.id} style={{ fontWeight: 500 }}>{a.title}</ActLink><div className="xs mut">{a.trackName}</div></td>
                  <td className="c-meta">{a.requestedBy?.name}</td>
                  <td className="c-meta mono xs"><span className="lab">asked </span>{a.requestedAt ? dayLabel(localDay(a.requestedAt)) : ''}</td>
                  <td className="c-meta mono xs" style={{ color: a.isOverdue ? 'var(--red-tx)' : undefined }}><span className="lab">promised </span>{a.dueDate ? dayLabel(a.dueDate) : a.startsAt ? dayLabel(localDay(a.startsAt)) : 'Not promised'}</td>
                  <td className="c-stat"><StatusPill status={a.status} overdue={a.isOverdue} /></td>
                </tr>))}
              </tbody></table></div>
          )}
        </div>
      )}

      {sheet === 'activity' && <NewActivity space={s} me={me} trackId={track === 'all' ? undefined : track} onClose={() => setSheet('none')} onNewTrack={can(me.role, 'track.write') ? () => setSheet('track') : undefined} />}
      {sheet === 'track' && <NewTrack spaceId={s.id} members={s.members} onClose={() => setSheet('none')} />}
      {sheet === 'members' && <MembersSheet spaceId={s.id} onClose={() => setSheet('none')} />}
    </>
  );
}

const IconSeen = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;

function SpaceSkeleton() {
  return (
    <div className="col gap12" role="status" aria-label="Loading">
      <Skel w="40%" h={28} r={8} />
      <div className="toprow">{[0, 1, 2].map(i => <div className="mini" key={i}><Skel w="50%" h={12} /><Skel h={12} /><Skel w="80%" h={12} /><Skel w="65%" h={12} /></div>)}</div>
      <div className="strip">{[0, 1, 2, 3].map(i => <div className="tk" key={i}><Skel w="70%" h={12} /><Skel w="50%" h={10} /><Skel h={4} /></div>)}</div>
      <SkelList rows={5} variant="conv" />
    </div>
  );
}
